import {
  ActionSubmit,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import { useNavigate } from "@tanstack/react-router"
import { isAxiosError } from "axios"
import type {
  ComponentProps,
  FormEventHandler,
} from "react"
import {
  useEffect,
  useCallback,
  useMemo,
  useState,
} from "react"
import { useIsMounted } from "usehooks-ts"
import styles from "./ResetPassword.module.scss"
import { useResetPassword } from "./useResetPassword"
import { useValidateToken } from "./useValidateToken"
import {
  PasswordForm,
  PublicPageHeader,
} from "src/components"
import {
  usePolyglot,
  useCountrySelectionContext,
} from "src/contexts"
import { useCountry } from "src/hooks"
import {
  ResetPasswordErrorToast,
  ResetPasswordTokenErrorToast,
  ResetPasswordSuccessToast,
} from "src/toasts"
import {
  injectProps,
  getSugarmateError,
} from "src/utils"

type Form = ComponentProps<typeof PasswordForm>["form"]

const polyglotPrefix = "pages.reset_password."

export function ResetPassword() {
  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const navigate = useNavigate()
  const isMounted = useIsMounted()
  const { setCountryCode } = useCountrySelectionContext()

  const countryCode = useMemo(
    () => {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get("country_code") ?? ""
    },
    [],
  )

  const country = useCountry(countryCode)
  const resetPassword = useResetPassword(country?.sugarmate_api_base_url)
  const validateToken = useValidateToken(country?.sugarmate_api_base_url)

  const [
    form,
    setForm,
  ] = useState<Form>({
    password: "",
    passwordConfirmation: "",
  })

  const updateForm = useCallback(
    (value: Partial<Form>) => {
      setForm({
        ...form,
        ...value,
      })
    },
    [
      form,
      setForm,
    ],
  )

  const delayedRedirect = useCallback(
    (path: "/forgot_password" | "/sign_in") => {
      setTimeout(
        () => {
          if (isMounted()) {
            void navigate({ to: path })
          }
        },
        5000,
      )
    },
    [
      isMounted,
      navigate,
    ],
  )

  const checkToken = useMemo(
    () => !validateToken
      ? undefined
      : async () => {
        try {
          await validateToken()
        } catch {
          showToast(
            "reset-password-token-error-toast",
            ResetPasswordTokenErrorToast,
          )

          delayedRedirect("/forgot_password")
        }
      },
    [
      showToast,
      validateToken,
      delayedRedirect,
    ],
  )

  useEffect(
    () => {
      void checkToken?.()
    },
    [checkToken],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        try {
          if (!resetPassword) {
            throw new Error("cannot reset password")
          }

          await resetPassword(form.password)

          showToast(
            "reset-password-error-toast",
            ResetPasswordSuccessToast,
          )

          if (country) {
            setCountryCode(country.country_code)
          }

          delayedRedirect("/sign_in")
        } catch (e) {
          // redirect back to forgot password page if link expired
          if (isAxiosError(e) && e.response?.status === 401) {
            showToast(
              "reset-password-token-error-toast",
              ResetPasswordTokenErrorToast,
            )

            delayedRedirect("/forgot_password")
          } else {
            const sugarmateError = getSugarmateError(e)

            const ErrorToast = injectProps(
              ResetPasswordErrorToast,
              {
                code: sugarmateError?.code,
                status: sugarmateError?.status,
              },
            )

            showToast(
              "reset-password-error-toast",
              ErrorToast,
            )
          }
        }
      })()
    },
    [
      form,
      resetPassword,
      showToast,
      delayedRedirect,
      country,
      setCountryCode,
    ],
  )

  return (
    <div className={styles.container}>
      <PublicPageHeader backRoutePath="/">
        {polyglot.t(`${polyglotPrefix}title`)}
      </PublicPageHeader>
      <PasswordForm
        form={form}
        updateForm={updateForm}
        onSubmit={onSubmit}
      >
        <ActionSubmit
          value={polyglot.t(`${polyglotPrefix}submit_cta`)}
          stylePreset="primary"
          className={styles.submit}
          disabled={!resetPassword}
          id="reset-password-button"
        />
      </PasswordForm>
    </div>
  )
}
